import React from 'react';
import { useI18n } from '@whizz/react-i18n';
import { FieldArray, Formik } from 'formik';
import classnames from 'classnames';
import type { ToggleModalProps } from '../../components/modal/types';
import type {
  ClassReportStudentType,
  ReportsCustomGroupFilterType,
  ReportsDefaultGroupFilterType,
  ReportsGenderFilterType,
  ReportsProgressionFilterType,
  ReportsQualityFilterType,
} from '../../pages/types';
import {
  reports_custom_group_filter,
  reports_default_group_filter,
  reports_gender_filter,
  reports_progression_filter,
  reports_quality_filter,
} from '../../pages/types';
import CancelButtonModal from '../buttons/CancelButtonModal';
import ConfirmButtonModal from '../buttons/ConfirmButtonModal';
import { Button, ChoiceGroup, Dropdown, Tag } from '../../components';
import {
  GENDER_FILTER_SCHEMA,
  GROUP_FILTER_SCHEMA,
  PROGRESSION_FILTER_SCHEMA,
  QUALITY_FILTER_SCHEMA,
  STUDENTS_FILTER_SCHEMA,
  TAG_FILTER_SCHEMA,
} from '../../forms/validations';
import { isEqual } from 'lodash';
import ClearButtonModal from '../buttons/ClearButtonModal';
import { ModalFooter } from '../../components/modal/Modal';
import type { Tag as TagType } from '@whizz/api-model/dist/@types';
import type { OptionProps, OptionTypeBase } from 'react-select';
import { components } from 'react-select';

export type ReportFilterStudentsValue = {
  student: number[],
};

export type ReportFilterGroupsValue = {
  group: ReportsDefaultGroupFilterType | ReportsCustomGroupFilterType | null,
};

export type ReportFilterQualityValue = {
  quality: ReportsQualityFilterType | null
};

export type ReportFilterProgressionValue = {
  progression: ReportsProgressionFilterType | null
};

export type ReportFilterGenderValue = {
  gender: ReportsGenderFilterType | null
};

export type ReportFilterTagValue = {
  tag: number | null
};

const ReportFilterStudents = (
  {
    students,
    toggle,
    initialValue,
    onSubmit,
    onClear,
  }: Pick<ToggleModalProps, 'toggle'> & {
    students: ClassReportStudentType[],
    initialValue: ReportFilterStudentsValue & { showOthersData: boolean }
    onSubmit(values: ReportFilterStudentsValue & { showOthersData: boolean }): void,
    onClear(): void,
  },
): JSX.Element => {
  const { translate } = useI18n();

  return (
    <Formik
      initialValues={initialValue}
      validateOnMount
      validationSchema={STUDENTS_FILTER_SCHEMA}
      onSubmit={(values) => onSubmit({ ...values })}
    >
      {({ values, handleSubmit, isValid, isSubmitting, isValidating, initialValues }) => (
        <form onSubmit={handleSubmit}>
          <div className={'overflow-auto p-md'} style={{ maxHeight: 400 }}>
            <FieldArray
              name={'student'}
              render={(arrayHelpers) => (
                <div className={'grid md:grid-cols-5 grid-flow-row auto-rows-max gap-2'} data-cy="field-array-students">
                  {[ ...students ]
                    .sort((a, b) => {
                      if (a.fullName < b.fullName) return -1;
                      if (a.fullName > b.fullName) return 1;
                      return 0;
                    })
                    .map((student) => (
                      <Button
                        key={`student_filter_${student.id}`}
                        color={'light'}
                        isOutline
                        highlight={values.student.includes(student.id)}
                        onClick={() => {
                          if (values.student.includes(student.id)) {
                            arrayHelpers.remove(values.student.indexOf(student.id));
                          } else {
                            arrayHelpers.push(student.id);
                          }
                        }}
                      >
                        {student.fullName}
                      </Button>
                    ))}
                </div>
              )}
            />
          </div>
          <ModalFooter>
            <div>
              <CancelButtonModal
                type={!isEqual(values, initialValues) ? 'reset' : 'button'}
                onClick={() => toggle(false)}
                disabled={isSubmitting || isValidating}
                data-cy="cancel-button-modal"
                className={'mr-5'}
              >
                {!isEqual(values, initialValues) ? translate('pages.REPORTS.FILTERS.MODAL.cancel_changes') : null}
              </CancelButtonModal>
              <ClearButtonModal
                onClick={onClear}
                disabled={0 == initialValue.student.length}
              />
            </div>
            <ConfirmButtonModal
              type={'submit'}
              disabled={!isValid || isSubmitting || isValidating}
              data-cy="confirm-button-modal"
            >
              {translate('pages.REPORTS.FILTERS.MODAL.confirm_changes')}
            </ConfirmButtonModal>
          </ModalFooter>
        </form>
      )}
    </Formik>
  );
};

const ReportFilterGroups = ({ toggle, initialValue, onSubmit, onClear }: Pick<ToggleModalProps, 'toggle'> & {
  initialValue: ReportFilterGroupsValue,
  onSubmit(values: ReportFilterGroupsValue): void,
  onClear(): void,
}): JSX.Element => {
  const { translate } = useI18n();
  return (
    <Formik
      initialValues={initialValue}
      initialTouched={{ group: false }}
      validateOnMount
      validationSchema={GROUP_FILTER_SCHEMA}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ touched, values, setFieldValue, setFieldTouched, handleSubmit, isValid, isSubmitting, isValidating }) => (
        <form onSubmit={handleSubmit}>
          <p className={'mb-md'}>
            {translate('pages.REPORTS.FILTERS.MODAL.group_default_description')}
          </p>
          <div className={'flex items-center mb-md'}>
            {reports_default_group_filter.map((group, index) => (
              <div
                key={`filter_group_${group}`}
                className={classnames({ 'mr-md': index != reports_default_group_filter.length - 1 })}
              >
                <ChoiceGroup
                  name={'filter_group'}
                  id={`filter_group_${group}`}
                  label={translate(`pages.REPORTS.FILTERS.MODAL.OPTIONS.GROUP.${group}`)}
                  propsForLabelWrapper={{
                    className: 'text-center bg-red-200 rounded py-1 px-3', // TODO Review colors
                  }}
                  type={'radio'}
                  checked={values.group === group}
                  onChange={() => {
                    setFieldTouched('group');
                    setFieldValue('group', group, true);
                  }}
                />
              </div>
            ))}
          </div>
          <p className={'mb-md'}>
            {translate('pages.REPORTS.FILTERS.MODAL.group_custom_description')}
          </p>
          {reports_custom_group_filter.map((group, index) => (
            <div
              key={`filter_group_${group}`}
              className={classnames({ 'mb-md': index != reports_custom_group_filter.length - 1 })}
            >
              <ChoiceGroup
                name={'filter_group'}
                id={`filter_group_${group}`}
                label={translate(`pages.REPORTS.FILTERS.MODAL.OPTIONS.GROUP.${group}`)}
                type={'radio'}
                checked={values.group === group}
                onChange={() => {
                  setFieldTouched('group');
                  setFieldValue('group', group, true);
                }}
              />
            </div>
          ))}
          <ModalFooter>
            <div>
              <CancelButtonModal
                type={touched.group ? 'reset' : 'button'}
                onClick={() => toggle(false)}
                disabled={isSubmitting || isValidating}
                data-cy="cancel-button-modal"
                className={'mr-5'}
              >
                {touched.group ? translate('pages.REPORTS.FILTERS.MODAL.cancel_changes') : null}
              </CancelButtonModal>
              <ClearButtonModal
                onClick={onClear}
                disabled={null == initialValue.group}
              />
            </div>
            <ConfirmButtonModal
              type={'submit'}
              disabled={!isValid || isSubmitting || isValidating}
              data-cy="confirm-button-modal"
            >
              {translate('pages.REPORTS.FILTERS.MODAL.confirm_changes')}
            </ConfirmButtonModal>
          </ModalFooter>
        </form>
      )}
    </Formik>
  );
};

const ReportFilterQuality = ({ toggle, initialValue, onSubmit, onClear }: Pick<ToggleModalProps, 'toggle'> & {
  initialValue: ReportFilterQualityValue,
  onSubmit(values: ReportFilterQualityValue): void,
  onClear(): void,
}): JSX.Element => {
  const { translate } = useI18n();
  return (
    <Formik
      initialValues={initialValue}
      initialTouched={{ quality: false }}
      validateOnMount
      validationSchema={QUALITY_FILTER_SCHEMA}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ touched, values, setFieldValue, setFieldTouched, handleSubmit, isValid, isSubmitting, isValidating }) => (
        <form onSubmit={handleSubmit}>
          <div className={'container'}>
            <p className={'mb-md'}>
              {translate('pages.REPORTS.FILTERS.MODAL.quality_description')}
            </p>
            {reports_quality_filter.map((quality, index) => (
              <div
                key={`filter_quality_${quality}`}
                className={classnames({ 'mb-md': index != reports_quality_filter.length - 1 })}
              >
                <ChoiceGroup
                  name={'filter_quality'}
                  id={`filter_quality_${quality}`}
                  label={translate(`pages.REPORTS.FILTERS.MODAL.OPTIONS.QUALITY.${quality}`)}
                  type={'radio'}
                  checked={values.quality === quality}
                  onChange={() => {
                    setFieldTouched('quality');
                    setFieldValue('quality', quality, true);
                  }}
                />
              </div>
            ))}
          </div>
          <ModalFooter>
            <div>
              <CancelButtonModal
                type={touched.quality ? 'reset' : 'button'}
                onClick={() => toggle(false)}
                disabled={isSubmitting || isValidating}
                data-cy="foo-modal-cancel-xx"
                className={'mr-5'}
              >
                {touched.quality ? translate('pages.REPORTS.FILTERS.MODAL.cancel_changes') : null}
              </CancelButtonModal>
              <ClearButtonModal
                onClick={onClear}
                disabled={null == initialValue.quality}
              />
            </div>
            <ConfirmButtonModal
              type={'submit'}
              disabled={!isValid || isSubmitting || isValidating}
            >
              {translate('pages.REPORTS.FILTERS.MODAL.confirm_changes')}
            </ConfirmButtonModal>
          </ModalFooter>
        </form>
      )}
    </Formik>
  );
};

const ReportFilterProgression = ({ toggle, initialValue, onSubmit, onClear }: Pick<ToggleModalProps, 'toggle'> & {
  initialValue: ReportFilterProgressionValue,
  onSubmit(values: ReportFilterProgressionValue): void,
  onClear(): void,
}): JSX.Element => {
  const { translate } = useI18n();
  return (
    <Formik
      initialValues={initialValue}
      initialTouched={{ progression: false }}
      validateOnMount
      validationSchema={PROGRESSION_FILTER_SCHEMA}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ touched, values, setFieldValue, setFieldTouched, handleSubmit, isValid, isSubmitting, isValidating }) => (
        <form onSubmit={handleSubmit}>
          <div className={'container'}>
            <p className={'mb-md'}>
              {translate('pages.REPORTS.FILTERS.MODAL.progression_description')}
            </p>
            {reports_progression_filter.map((progression, index) => (
              <div
                key={`filter_progression_${progression}`}
                className={classnames({ 'mb-md': index != reports_progression_filter.length - 1 })}
              >
                <ChoiceGroup
                  name={'filter_progression'}
                  id={`filter_progression_${progression}`}
                  label={translate(`pages.REPORTS.FILTERS.MODAL.OPTIONS.PROGRESSION.${progression}`)}
                  type={'radio'}
                  checked={values.progression === progression}
                  onChange={() => {
                    setFieldTouched('progression');
                    setFieldValue('progression', progression, true);
                  }}
                />
              </div>
            ))}
          </div>
          <ModalFooter>
            <div>
              <CancelButtonModal
                type={touched.progression ? 'reset' : 'button'}
                onClick={() => toggle(false)}
                disabled={isSubmitting || isValidating}
                className={'mr-5'}
              >
                {touched.progression ? translate('pages.REPORTS.FILTERS.MODAL.cancel_changes') : null}
              </CancelButtonModal>
              <ClearButtonModal
                onClick={onClear}
                disabled={null == initialValue.progression}
              />
            </div>
            <ConfirmButtonModal
              type={'submit'}
              disabled={!isValid || isSubmitting || isValidating}
            >
              {translate('pages.REPORTS.FILTERS.MODAL.confirm_changes')}
            </ConfirmButtonModal>
          </ModalFooter>
        </form>
      )}
    </Formik>
  );
};

const ReportFilterGender = ({ toggle, initialValue, onSubmit, onClear }: Pick<ToggleModalProps, 'toggle'> & {
  initialValue: ReportFilterGenderValue,
  onSubmit(values: ReportFilterGenderValue): void,
  onClear(): void,
}): JSX.Element => {
  const { translate } = useI18n();
  return (
    <Formik
      initialValues={initialValue}
      initialTouched={{ gender: false }}
      validateOnMount
      validationSchema={GENDER_FILTER_SCHEMA}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ touched, values, setFieldValue, setFieldTouched, handleSubmit, isValid, isSubmitting, isValidating }) => (
        <form onSubmit={handleSubmit}>
          <div className={'container'}>
            <p className={'mb-md'}>
              {translate('pages.REPORTS.FILTERS.MODAL.gender_description')}
            </p>
            {reports_gender_filter.map((gender, index) => (
              <div
                key={`filter_gender_${gender}`}
                className={classnames({ 'mb-md': index != reports_gender_filter.length - 1 })}
              >
                <ChoiceGroup
                  name={'filter_gender'}
                  id={`filter_gender_${gender}`}
                  icon={'Student3d'}
                  iconColor={gender}
                  label={translate(`pages.REPORTS.FILTERS.MODAL.OPTIONS.GENDER.${gender}`)}
                  type={'radio'}
                  checked={values.gender === gender}
                  onChange={() => {
                    setFieldTouched('gender');
                    setFieldValue('gender', gender, true);
                  }}
                />
              </div>
            ))}
          </div>
          <ModalFooter>
            <div>
              <CancelButtonModal
                type={touched.gender ? 'reset' : 'button'}
                onClick={() => toggle(false)}
                disabled={isSubmitting || isValidating}
                className={'mr-5'}
              >
                {touched.gender ? translate('pages.REPORTS.FILTERS.MODAL.cancel_changes') : null}
              </CancelButtonModal>
              <ClearButtonModal
                onClick={onClear}
                disabled={null == initialValue.gender}
              />
            </div>
            <ConfirmButtonModal
              type={'submit'}
              disabled={!isValid || isSubmitting || isValidating}
            >
              {translate('pages.REPORTS.FILTERS.MODAL.confirm_changes')}
            </ConfirmButtonModal>
          </ModalFooter>
        </form>
      )}
    </Formik>
  );
};

const ReportFilterTag = ({ tags, toggle, initialValue, onSubmit, onClear }: Pick<ToggleModalProps, 'toggle'> & {
  tags: TagType[],
  initialValue: ReportFilterTagValue,
  onSubmit(values: ReportFilterTagValue): void,
  onClear(): void,
}): JSX.Element => {
  const { translate } = useI18n();
  const translatedTags: TagType[] = tags.map((tag) => ({
    ...tag,
    abbreviation: translate(`pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS_ABBREVIATIONS.${tag.abbreviation}`, { 'defaultValue': tag.abbreviation }),
    description: translate(`pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS_DESCRIPTIONS.${tag.abbreviation}`, { 'defaultValue': tag.description }),
  }));

  return (
    <Formik
      initialValues={initialValue}
      initialTouched={{ tag: false }}
      validateOnMount
      validationSchema={TAG_FILTER_SCHEMA}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ touched, values, setFieldValue, setFieldTouched, handleSubmit, isValid, isSubmitting, isValidating }) => {
        return (
          <form onSubmit={handleSubmit}>
            <div className={'container ml-0 w-full md:w-3/4'}>
              <Dropdown
                buttonPlaceholder={translate('pages.REPORTS.FILTERS.MODAL.tag_description')}
                align={'space-between'}
                size={'lg'}
                options={translatedTags.map((tag) => ({
                  label: tag.abbreviation,
                  description: tag.description,
                  color: tag.color,
                  value: tag.id,
                }))}
                disableSearch
                value={values.tag}
                handleChange={(option: { value: number }) => {
                  setFieldTouched('tag');
                  setFieldValue('tag', option.value, true);
                }}
                componentsForSelect={{
                  Option: (props: OptionProps<OptionTypeBase, false> & TagType) => <TagOption {...props} />,
                  NoOptionsMessage: () => (
                    <div className={'m-md text-center text-xl'}>
                      <p>{translate('components.SELECTORS.NO_OPTIONS.TAGS.LINE1')}</p>
                      <p>{translate('components.SELECTORS.NO_OPTIONS.TAGS.LINE2')}</p>
                    </div>),
                }}
                customValueToShow={values.tag ? <TagSelected {...(translatedTags.find((tag) => tag.id === values.tag) as TagType)} /> : null}
              />
            </div>
            <ModalFooter>
              <div>
                <CancelButtonModal
                  type={touched.tag ? 'reset' : 'button'}
                  onClick={() => toggle(false)}
                  disabled={isSubmitting || isValidating}
                  className={'mr-5'}
                >
                  {touched.tag ? translate('pages.REPORTS.FILTERS.MODAL.cancel_changes') : null}
                </CancelButtonModal>
                <ClearButtonModal
                  onClick={onClear}
                  disabled={null == initialValue.tag}
                />
              </div>
              <ConfirmButtonModal
                type={'submit'}
                disabled={values.tag == null || !isValid || isSubmitting || isValidating}
              >
                {translate('pages.REPORTS.FILTERS.MODAL.confirm_changes')}
              </ConfirmButtonModal>
            </ModalFooter>
          </form>
        );}}
    </Formik>
  );
};

const TagOption = (props: OptionProps<OptionTypeBase, false> & TagType) => {
  const { label, color, description } = props.data;
  return (
    <components.Option {...props}>
      <div className={'flex items-center p-sm'}>
        <Tag color={color} className={'mr-sm'}>{label}</Tag>
        {description}
      </div>
    </components.Option>
  );
};

const TagSelected = (props: TagType) => {
  const { abbreviation, color, description } = props;
  return (
    <div className={'flex items-center'}>
      <Tag color={color} className={'mr-sm'}>{abbreviation}</Tag>
      {description}
    </div>
  );
};

export { ReportFilterStudents, ReportFilterGroups, ReportFilterQuality, ReportFilterProgression, ReportFilterGender, ReportFilterTag };
